// SB Remediation tab on the diagnostics report.
//
// Read-only on purpose. This is the reporting view of the same data the
// add-activity picker assigns from, and a report is somewhere a tutor looks
// while talking to a parent, not somewhere they should be able to change a
// child's lesson by mis-clicking. Assigning stays where it belongs, on the
// add-activity page. Preview may follow.
//
// The stats strip and the row shape are shared with the picker
// (BmdSkillbuilderStats, utils/skillbuilder-rows) so the report cannot show a
// different number of outstanding worksheets from the page that assigns them.

import React, { useEffect, useMemo, useState } from "react"
import { Badge, Table } from "reactstrap"
import { useBmdSkillbuilderPicker } from "../hooks/useBmdSkillbuilders"
import { BmdSkillbuilderKpiCards } from "./BmdSkillbuilderKpiCards"
import {
  allRows,
  groupRowsByStrand,
  rowPathParts,
} from "../utils/skillbuilder-rows"
import type { BmdSkillbuilderItem } from "../types/bmd-skillbuilder.types"

type Props = {
  studentKey: string
  /**
   * The diagnostic being reported on, or null for "All sessions" - which is a
   * real view here, not an empty state. Every other tab honours the header's
   * scope selector, and this one used to refuse and show a prompt instead.
   */
  diagnosticsSessionKey: string | null
}

const FILTER_ALL = "all"

/**
 * Whether a row belongs in the current status filter.
 *
 * COMPLETED MEANS SUBMITTED, NOTHING MORE. The server sets it from the
 * assignment row alone - `statusOf` never looks at the score - and the
 * "Completed" card counts it the same way. This filter used to require
 * `passed !== false` as well, which read as tidy ("a failed sheet isn't really
 * completed") and was wrong twice over: a worksheet the student had finished
 * and scored 60% on vanished from Completed, and the card above the table said
 * 1 while the table said none.
 *
 * "Under the pass mark" is therefore NOT a fourth status. Such a row is
 * completed AND under the mark, and appears under both filters, which is what
 * the badge in the row already says.
 */
function matchesStatus(item: BmdSkillbuilderItem, filter: string): boolean {
  if (filter === FILTER_ALL) return true
  if (filter === "under_pass") return item.passed === false
  if (filter === "no_worksheet") return !item.has_skillbuilder
  if (!item.has_skillbuilder) return false
  if (filter === "completed") return item.status === "COMPLETED"
  if (filter === "assigned") return item.status === "ASSIGNED"
  if (filter === "not_assigned") return item.status === "NOT_ASSIGNED"
  return true
}

/** Same rules as the picker's badge, kept in one place would be better still. */
function statusBadge(item: BmdSkillbuilderItem): React.ReactNode {
  if (!item.has_skillbuilder) {
    return <span className="text-muted font-size-12">Not written yet</span>
  }
  // The VERDICT is read before the activity status: `passed` comes from a real
  // marked attempt, while the status flag is a separate write that can lag it.
  if (item.passed === false) {
    return (
      <Badge className="bg-danger font-size-11">
        Under {item.pass_percent}%
      </Badge>
    )
  }
  if (item.status === "COMPLETED") {
    return <Badge className="bg-success font-size-11">Completed</Badge>
  }
  if (item.status === "ASSIGNED") {
    return <Badge className="bg-info font-size-11">Assigned</Badge>
  }
  return <span className="text-muted font-size-12">Not assigned</span>
}

export const BmdSbRemediationTab: React.FC<Props> = ({
  studentKey,
  diagnosticsSessionKey,
}) => {
  const { picker, loading, error } = useBmdSkillbuilderPicker(
    diagnosticsSessionKey || "",
    studentKey
  )

  // Flat, ordered by strand then year: this is a table to read down, not a tree
  // to open. The strand is a column rather than a heading for the same reason.
  const rows = useMemo(
    () => allRows(groupRowsByStrand(picker?.years || [])),
    [picker]
  )

  const [strandFilter, setStrandFilter] = useState<string>(FILTER_ALL)
  const [yearFilter, setYearFilter] = useState<string>(FILTER_ALL)
  const [statusFilter, setStatusFilter] = useState<string>(FILTER_ALL)

  // Options come from the rows actually present, so a tutor is never offered a
  // filter that would empty the table.
  const strandOptions = useMemo(
    () => Array.from(new Set(rows.map(r => r.strand_name))).filter(Boolean),
    [rows]
  )
  // Ordered by the curriculum, not alphabetically: "Year 10" must not sit
  // between "Year 1" and "Year 2", and Reception comes before all of them.
  const yearOptions = useMemo(() => {
    const seen = new Map<string, number>()
    for (const r of rows) {
      if (r.year_name && !seen.has(r.year_name))
        seen.set(r.year_name, r.year_order)
    }
    return Array.from(seen.entries())
      .sort((a, b) => a[1] - b[1])
      .map(([name]) => name)
  }, [rows])

  // A filter left pointing at a value that no longer exists would show an empty
  // table with no clue why, so it falls back to All when its option goes away.
  useEffect(() => {
    if (strandFilter !== FILTER_ALL && !strandOptions.includes(strandFilter)) {
      setStrandFilter(FILTER_ALL)
    }
  }, [strandOptions, strandFilter])
  useEffect(() => {
    if (yearFilter !== FILTER_ALL && !yearOptions.includes(yearFilter)) {
      setYearFilter(FILTER_ALL)
    }
  }, [yearOptions, yearFilter])

  const visible = useMemo(
    () =>
      rows.filter(r => {
        if (strandFilter !== FILTER_ALL && r.strand_name !== strandFilter) {
          return false
        }
        if (yearFilter !== FILTER_ALL && r.year_name !== yearFilter)
          return false
        return matchesStatus(r.item, statusFilter)
      }),
    [rows, strandFilter, yearFilter, statusFilter]
  )

  if (loading) {
    return (
      <div className="bg-white rounded shadow-sm p-5 text-center text-muted">
        Loading…
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded shadow-sm p-5 text-center text-danger">
        {error}
      </div>
    )
  }

  return (
    <div className="bg-white rounded shadow-sm p-3">
      <BmdSkillbuilderKpiCards
        counts={picker?.counts}
        passPercent={picker?.pass_percent ?? 75}
      />

      {/* Same filter idiom as the Skills Gap tab: small labelled selects, an
          "All" default, and a count of what survived, so the tutor can always
          tell an empty table from a filtered one. */}
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <div className="d-flex flex-wrap gap-3 align-items-center">
          <div className="d-flex align-items-center">
            <label
              htmlFor="bmd-sb-strand-filter"
              className="text-muted font-size-12 me-2 mb-0"
            >
              Cluster:
            </label>
            <select
              id="bmd-sb-strand-filter"
              className="form-select form-select-sm"
              style={{ minWidth: 160 }}
              value={strandFilter}
              onChange={e => setStrandFilter(e.target.value)}
              disabled={strandOptions.length === 0}
            >
              <option value={FILTER_ALL}>All</option>
              {strandOptions.map(name => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          <div className="d-flex align-items-center">
            <label
              htmlFor="bmd-sb-year-filter"
              className="text-muted font-size-12 me-2 mb-0"
            >
              Year:
            </label>
            <select
              id="bmd-sb-year-filter"
              className="form-select form-select-sm"
              style={{ minWidth: 110 }}
              value={yearFilter}
              onChange={e => setYearFilter(e.target.value)}
              disabled={yearOptions.length === 0}
            >
              <option value={FILTER_ALL}>All</option>
              {yearOptions.map(name => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          <div className="d-flex align-items-center">
            <label
              htmlFor="bmd-sb-status-filter"
              className="text-muted font-size-12 me-2 mb-0"
            >
              Status:
            </label>
            <select
              id="bmd-sb-status-filter"
              className="form-select form-select-sm"
              style={{ minWidth: 150 }}
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value={FILTER_ALL}>All</option>
              <option value="not_assigned">Not assigned</option>
              <option value="assigned">Assigned</option>
              <option value="completed">Completed</option>
              <option value="under_pass">
                Under {picker?.pass_percent ?? 75}%
              </option>
              <option value="no_worksheet">Not written yet</option>
            </select>
          </div>
        </div>

        <div className="text-muted font-size-12">
          Showing {visible.length} of {rows.length} skill builder
          {rows.length === 1 ? "" : "s"}
        </div>
      </div>

      {visible.length === 0 ? (
        <p className="text-muted text-center mb-0 py-4">
          {rows.length === 0
            ? diagnosticsSessionKey
              ? "This diagnostic found no skills needing remediation."
              : "No diagnostic has found a skill needing remediation yet."
            : "Nothing matches these filters."}
        </p>
      ) : (
        <div className="table-responsive">
          {/* NOT table-nowrap: the path under each skill name is long enough
              to widen the table past its container, which pushes the last
              columns out of reach. Fixed layout keeps the declared widths. */}
          <Table
            className="align-middle mb-0"
            style={{ tableLayout: "fixed", width: "100%" }}
          >
            <thead className="table-light">
              <tr>
                <th style={{ width: 170 }}>
                  <h6 className="text-dark mb-0">Cluster</h6>
                </th>
                <th>
                  <h6 className="text-dark mb-0">Skill Builders</h6>
                </th>
                <th style={{ width: 120 }}>
                  <h6 className="text-dark mb-0">Year level</h6>
                </th>
                <th style={{ width: 160 }}>
                  <h6 className="text-dark mb-0">Status</h6>
                </th>
                <th style={{ width: 90 }}>
                  <h6 className="text-dark mb-0">Score</h6>
                </th>
                <th style={{ width: 90 }}>
                  <h6 className="text-dark mb-0">Attempts</h6>
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map(row => (
                <tr
                  key={`${row.item.skill_id}-${row.year_level_id}`}
                  className={row.item.has_skillbuilder ? "" : "text-muted"}
                >
                  <td
                    className="font-size-13"
                    style={{ whiteSpace: "normal", wordBreak: "break-word" }}
                  >
                    {row.strand_name}
                  </td>
                  <td style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                    {/* The SKILL is the name a tutor knows a worksheet by. The
                        stored skillbuilder_name is an S3 key and is never
                        shown. */}
                    <div className="font-size-13">
                      <span className="text-muted fw-500 me-1">Skill:</span>
                      {row.item.skill_name}
                    </div>
                    <div className="text-muted font-size-12">
                      {/* Every level, each labelled. Two of them often hold the same
              words in this curriculum, so the labels are what tells them
              apart. */}
                      {rowPathParts(row).map((part, i) => (
                        <span key={part.label}>
                          {i > 0 ? " · " : ""}
                          {part.label === "Year" ? null : (
                            <span className="fw-500">{part.label}: </span>
                          )}
                          {part.value}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="font-size-13">{row.year_name || "—"}</td>
                  <td>{statusBadge(row.item)}</td>
                  <td className="font-size-13">
                    {row.item.score_percent != null
                      ? `${row.item.score_percent}%`
                      : "—"}
                  </td>
                  <td className="font-size-13">
                    {row.item.attempt_count > 0 ? row.item.attempt_count : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  )
}

export default BmdSbRemediationTab
